import { tokenStore } from '@/lib/api'

const WS_BASE_URL = (() => {
  const explicit =
    (import.meta.env.VITE_WS_BASE_URL as string | undefined) ?? ''
  if (explicit) return explicit.replace(/\/$/, '')
  const apiBase =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
    'http://localhost:8000'
  return apiBase.replace(/^http/, 'ws').replace(/\/$/, '')
})()

const WS_PATH = '/ws/admin/'

export type AdminEvent = {
  type: string
  group?: string
  [key: string]: unknown
}
export type EventListener = (event: AdminEvent) => void
export type ConnectionStatus =
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'offline'
export type StatusListener = (status: ConnectionStatus) => void

const RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 32_000]

class AdminSocket {
  private socket: WebSocket | null = null
  private status: ConnectionStatus = 'offline'
  private reconnectAttempt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private groups = new Map<string, Set<EventListener>>()
  private statusListeners = new Set<StatusListener>()
  private explicitlyClosed = false

  getStatus(): ConnectionStatus {
    return this.status
  }

  onStatusChange(listener: StatusListener): () => void {
    this.statusListeners.add(listener)
    listener(this.status)
    return () => this.statusListeners.delete(listener)
  }

  subscribe(group: string, listener: EventListener): () => void {
    let set = this.groups.get(group)
    if (!set) {
      set = new Set()
      this.groups.set(group, set)
      this.send({ type: 'subscribe', group })
    }
    set.add(listener)

    if (!this.socket && !this.reconnectTimer) {
      this.connect()
    }

    return () => {
      const current = this.groups.get(group)
      if (!current) return
      current.delete(listener)
      if (current.size === 0) {
        this.groups.delete(group)
        this.send({ type: 'unsubscribe', group })
      }
    }
  }

  connect(): void {
    if (this.socket) return
    const token = tokenStore.getAccess()
    if (!token) {
      this.setStatus('offline')
      return
    }
    this.explicitlyClosed = false
    this.setStatus(this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting')
    try {
      this.socket = new WebSocket(`${WS_BASE_URL}${WS_PATH}`, ['jwt', token])
    } catch {
      this.scheduleReconnect()
      return
    }
    this.socket.onopen = () => this.handleOpen()
    this.socket.onmessage = (e) => this.handleMessage(e)
    this.socket.onerror = () => this.handleError()
    this.socket.onclose = () => this.handleClose()
  }

  disconnect(): void {
    this.explicitlyClosed = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.socket?.close()
    this.socket = null
    this.setStatus('offline')
  }

  private handleOpen(): void {
    this.reconnectAttempt = 0
    this.setStatus('live')
    for (const group of this.groups.keys()) {
      this.send({ type: 'subscribe', group })
    }
  }

  private handleMessage(event: MessageEvent): void {
    let payload: AdminEvent
    try {
      payload = JSON.parse(event.data) as AdminEvent
    } catch {
      return
    }
    if (!payload?.group) return
    const set = this.groups.get(payload.group)
    if (!set) return
    for (const listener of set) listener(payload)
  }

  private handleError(): void {
    this.setStatus('reconnecting')
  }

  private handleClose(): void {
    this.socket = null
    if (this.explicitlyClosed) return
    this.scheduleReconnect()
  }

  private scheduleReconnect(): void {
    const delay =
      RECONNECT_DELAYS_MS[
        Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)
      ]
    this.setStatus('reconnecting')
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.reconnectAttempt += 1
      this.connect()
    }, delay)
  }

  private send(payload: object): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload))
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return
    this.status = status
    for (const listener of this.statusListeners) listener(status)
  }
}

export const adminSocket = new AdminSocket()

export function connectAdminSocket(): void {
  adminSocket.connect()
}

export function disconnectAdminSocket(): void {
  adminSocket.disconnect()
}
